import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { ThemeNames } from './ITheme.js';

var palette = {
  accent1Darkest: '#000918',
  accent1Darker: '#001231',
  accent1Dark: '#001c49',
  accent1: '#002663',
  accent1Light: '#3f5c8a',
  accent1Lighter: '#7e91b0',
  accent1Lightest: '#bec7d7',
  accent2Darkest: '#181819',
  accent2Darker: '#303032',
  accent2Dark: '#48494b',
  accent2: '#616265',
  accent2Light: '#88898b',
  accent2Lighter: '#afafb1',
  accent2Lightest: '#d6d7d7',
  black: '#000000',
  errorDarkest: '#300809',
  errorDarker: '#601013',
  errorDark: '#91181d',
  error: '#c32127',
  errorLight: '#d2585d',
  errorLighter: '#e08f92',
  errorLightest: '#efc6c8',
  grayDarkest: '#363636',
  grayDarker: '#6b6b6b',
  grayDark: '#a1a1a1',
  gray: '#d8d8d8',
  grayLight: '#e1e1e1',
  grayLighter: '#ebebeb',
  grayLightest: '#f5f5f5',
  inherit: 'inherit',
  primaryDarkest: '#361e08',
  primaryDarker: '#6b3c11',
  primaryDark: '#a15b1a',
  primary: '#f89b06',
  primaryLight: '#e29b5a',
  primaryLighter: '#ebbb90',
  primaryLightest: '#f5ddc7',
  secondaryDarkest: '#061d2e',
  secondaryDarker: '#0d3a5c',
  secondaryDark: '#14578b',
  secondary: '#23669a',
  secondaryLight: '#5497cc',
  secondaryLighter: '#8cb9dc',
  secondaryLightest: '#d8e8f3',
  successDarkest: '#1b250f',
  successDarker: '#35491d',
  successDark: '#4c6a2a',
  success: '#6c953c',
  successLight: '#90af6c',
  successLighter: '#b4c99c',
  successLightest: '#d9e4cd',
  warningDarker: '#755b11',
  warningDarkest: '#3b2e08',
  warningDark: '#b08a1a',
  warning: '#222222',
  warningLight: '#f0ca5a',
  warningLighter: '#f5db90',
  warningLightest: '#faedc7',
  white: '#ffffff'
};
var filledDisabled = {
  actionStyle: {},
  bgColor: '#D8D8D8',
  borderColor: '#D8D8D8',
  txtColor: '#fff'
};
var outlinedDisabled = {
  actionStyle: {},
  bgColor: '#fff',
  borderColor: '#D8D8D8',
  txtColor: '#D8D8D8'
};
var baseTheme = {
  boxShadow: {
    box: '0 2px 3px rgba(34, 34, 34, .3)',
    button: '0 2px 3px rgba(34, 34, 34, .3)',
    buttonFocus: '0 0 3px #1b75bb'
  },
  button: {
    primary: {
      actionStyle: {
        background: 'hsl(208.63, 85%, 25%)',
        border: '1px solid hsl(208.63, 85%, 25%)'
      },
      border: 'none',
      bgColor: 'hsl(208.63, 85%, 35%)',
      borderColor: 'hsl(208.63, 85%, 35%)',
      disabled: filledDisabled,
      txtColor: palette.white
    },
    primaryAlt: {
      actionStyle: {
        background: 'hsl(208.63, 85%, 25%)',
        border: '1px solid hsl(208.63, 85%, 25%)'
      },
      border: 'none',
      bgColor: 'hsl(208.63, 85%, 35%)',
      borderColor: 'hsl(208.63, 85%, 35%)',
      disabled: filledDisabled,
      txtColor: palette.white
    },
    secondary: {
      actionStyle: {
        border: '1px solid #036d9b'
      },
      border: 'none',
      bgColor: palette.white,
      borderColor: '#036d9b',
      disabled: outlinedDisabled,
      txtColor: '#036d9b'
    },
    text: {
      border: 'none',
      actionStyle: {
        border: '1px solid transparent'
      },
      bgColor: 'transparent',
      borderColor: 'transparent',
      disabled: {
        actionStyle: {},
        bgColor: 'transparent',
        borderColor: 'transparent',
        txtColor: '#D8D8D8'
      },
      txtColor: '#036D9B'
    },
    white: {
      actionStyle: {
        border: '2px solid #fff'
      },
      bgColor: 'transparent',
      borderColor: palette.white,
      disabled: outlinedDisabled,
      txtColor: palette.white
    }
  },
  link: {
    color: '#036D9B'
  }
};
var defaultTheme = _objectSpread2({
  name: ThemeNames["default"],
  fontFamily: '"Roboto", helvetica, sans-serif',
  palette: palette,
  grid: {
    containerWidth: {
      small: '1140px',
      medium: '1140px',
      large: '1140px'
    }
  },
  border: {
    radius: {
      large: '12px',
      medium: '6px',
      small: '3px'
    },
    size: {
      large: '8px',
      medium: '2px',
      small: '1px'
    },
    style: 'solid'
  },
  boxShadowSecondary: '0 2px 3px rgba(0,0,0,.3)',
  fontSize: {
    large: '18px',
    medium: '16px',
    small: '12px'
  },
  headings: {
    fontFamily: '"Roboto", helvetica, sans-serif'
  },
  input: {
    backgroundColor: 'white',
    focusBorderColor: 'secondary'
  },
  stepper: {
    step: {
      active: {
        backgroundColor: palette.secondaryDark,
        borderColor: palette.secondaryDark,
        color: palette.white,
        borderLeft: "0.3rem solid ".concat(palette.secondaryDark),
        borderBottom: "0.9rem solid ".concat(palette.secondaryDark),
        borderTop: "0.9rem solid ".concat(palette.secondaryDark)
      },
      completed: {
        backgroundColor: palette.secondaryDark,
        borderColor: palette.secondaryDark,
        color: palette.white,
        borderLeft: "0.3rem solid ".concat(palette.secondaryDark),
        borderBottom: "0.9rem solid ".concat(palette.secondaryDark),
        borderTop: "0.9rem solid ".concat(palette.secondaryDark)
      }
    }
  },
  table: {
    backgroundColor: 'white',
    cellPaddingTop: '1rem',
    cellPaddingRight: '1rem',
    cellPaddingBottom: '1rem',
    cellPaddingLeft: '1rem',
    rowBandingColor: 'grayLightest'
  },
  tabs: {
    backgroundActive: "linear-gradient(to top, #ffffff 90%, #002663 10%)",
    background: palette.gray,
    borderTopLeftRadius: '0.1875rem',
    borderTopRightRadius: '0.1875rem',
    borderBottom: 'none',
    borderActive: "1px solid ".concat(palette.gray),
    fullWidth: false,
    marginRight: '0.5rem',
    fontWeight: 'bold'
  },
  modal: {
    headerDividerColor: 'grayLight',
    footerDividerColor: 'grayLight',
    headerPadding: '1.875rem 0rem 1.5625rem 0rem',
    footerPadding: '1.875rem 0rem 1.5625rem 0rem'
  }
}, baseTheme);

export { baseTheme, defaultTheme, filledDisabled, outlinedDisabled };
